import moment from 'moment';

// Formatting follows the standard locale data (CLDR) of the browser for the regional formats the BE offers (systemLocales)

export const DATE_STYLE = {
  // e.g. 05.09.2026, 09/05/2026
  NUMERIC: 'NUMERIC',
  // e.g. 5. September 2026, September 5, 2026
  WRITTEN: 'WRITTEN',
};

const DATE_STYLE_OPTIONS = {
  [DATE_STYLE.NUMERIC]: { year: 'numeric', month: '2-digit', day: '2-digit' },
  [DATE_STYLE.WRITTEN]: { dateStyle: 'long' },
};

// amounts from 1,000 on can be shown in short form (charts, tiles, summary figures)
export const COMPACT_THRESHOLD = 1000;

/**
 * BCP 47 tag of a regional format, e.g. 'de-AT'
 * @param {Object} locale locale config of the BE ({ id, tag }), e.g. tag 'DE-at'
 */
const toLanguageTag = (locale) => {
  if (!locale?.tag) {
    throw new Error(`Locale ${locale?.id ?? locale} has no tag`);
  }
  return Intl.getCanonicalLocales(locale.tag)[0];
};

const isZeroWhenRounded = (amount, fractionDigits) => Math.abs(Number(amount)) < 0.5 * 10 ** -fractionDigits;

// German has no short form for thousands in CLDR (1200 stays 1.200 €), we show 1,2 Tsd. € anyway
const formatGermanThousands = (amount, currencyCode, tag) => {
  const formatter = new Intl.NumberFormat(tag, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  const parts = formatter.formatToParts(Number(amount) / 1000);
  // the abbreviation goes right after the number, e.g. 1,2 Tsd. € or € 1,2 Tsd.
  const numberPartTypes = ['integer', 'group', 'decimal', 'fraction'];
  const lastIndex = Math.max(...numberPartTypes.map((type) => parts.map((part) => part.type).lastIndexOf(type)));
  return parts.map((part, index) => (index === lastIndex ? `${part.value}\u00a0Tsd.` : part.value)).join('');
};

/**
 * The single formatter for currency amounts.
 * Full amounts always have 2 decimals, halves round away from zero and a zero result has no minus sign.
 *
 * @param {String|Number} amount preferably a decimal string, e.g. '1234.50'
 * @param {String} currencyCode ISO code, e.g. 'EUR'
 * @param {Object} locale regional format (locale config of the BE)
 * @param {Object} options compact: short form for amounts from 1,000 on, e.g. 1,2 Mio. €, €1.2M
 * @returns {String} e.g. 1.234,50 €, € 1.234,50, $1,234.50, CHF 1'234.50
 */
export const formatCurrency = (amount, currencyCode, locale, { compact = false } = {}) => {
  if (amount === null || amount === undefined || amount === '' || Number.isNaN(Number(amount))) return '';
  if (!currencyCode) throw new Error('Missing currency');

  const tag = toLanguageTag(locale);
  const absolute = Math.abs(Number(amount));

  if (compact && absolute >= COMPACT_THRESHOLD) {
    if (tag.startsWith('de-') && Math.round(absolute / 100) < 10000) {
      return formatGermanThousands(amount, currencyCode, tag);
    }
    return new Intl.NumberFormat(tag, {
      style: 'currency',
      currency: currencyCode,
      notation: 'compact',
      compactDisplay: 'short',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(amount);
  }

  return new Intl.NumberFormat(tag, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(isZeroWhenRounded(amount, 2) ? 0 : amount);
};

/**
 * Plain numbers (no currency), e.g. percentages: 12,5 or 12.5
 */
export const formatNumber = (value, locale, { minimumFractionDigits = 0, maximumFractionDigits = 2 } = {}) => {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return '';
  return new Intl.NumberFormat(toLanguageTag(locale), { minimumFractionDigits, maximumFractionDigits }).format(
    isZeroWhenRounded(value, maximumFractionDigits) ? 0 : value
  );
};

/**
 * Separators of the regional format, e.g. for parsing user input: { group: '.', decimal: ',' }
 */
export const getNumberSeparators = (locale) => {
  const parts = new Intl.NumberFormat(toLanguageTag(locale)).formatToParts(12345.6);
  return {
    group: parts.find((part) => part.type === 'group')?.value ?? '',
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
  };
};

/**
 * The single formatter for dates
 * @param {Date|String|moment} date
 * @param {Object} locale regional format (locale config of the BE)
 * @param {String} style DATE_STYLE.NUMERIC (05.09.2026) or DATE_STYLE.WRITTEN (5. September 2026)
 */
export const formatDate = (date, locale, style = DATE_STYLE.NUMERIC) => {
  if (date === undefined || date === null || date === '') return '';
  const value = moment(date);
  if (!value.isValid()) return '';
  return new Intl.DateTimeFormat(toLanguageTag(locale), DATE_STYLE_OPTIONS[style]).format(value.toDate());
};

/**
 * The expiration date of something that runs for a year from the given date (one year later, minus a day)
 */
export const formatExpirationDate = (date, locale, style = DATE_STYLE.NUMERIC) => {
  if (date === undefined || date === null || date === '') return '';
  const value = moment(date);
  if (!value.isValid()) return '';
  return formatDate(value.add(1, 'y').subtract(1, 'd'), locale, style);
};
